<template>
  <DxPopup
    :visible.sync="previewVisible"
    :drag-enabled="false"
    :hide-on-outside-click="true"
    :show-title="true"
    :title="previewTitle"
    :width="650"
    :height="610"
    :showCloseButton="false"
    @hidden="popupHidden"
    :fullScreen="false"
    ref="gc-popup"
    :wrapperAttr="{ class: 'css-v2 gc-popup gc-popup-image-preview' }"
  >
    <div class="css-v2 gc-popup-form">
      <img
        v-if="isImageContent"
        id="imagePreview"
        :src="this.previewImageSource"
        alt="Company Logo"
        height="500"
        width="520"
      />
      <iframe
        v-else-if="isPdfContent"
        id="pdfPreview"
        :src="previewImageSource"
        width="520"
        height="500"
      ></iframe>
      <div v-else class="gcpi-doc-placeholder" id="documentPreview">
        <div class="gcpi-doc-icon" v-html="documentIcon"></div>
        <div class="gcpi-doc-filename">{{ dataModel.FileName }}</div>
      </div>
    </div>

    <div class="footer" v-bind:class="'multi-btn'">
      <DxButton
        v-if="saveButtonConfig"
        :text="saveButtonConfig.text"
        :type="saveButtonConfig.type"
        styling-mode="contained"
        @click="saveButtonConfig.clickCallback"
        :disabled="saveButtonConfig.disabled"
      />
      <DxButton
        v-if="removeButtonConfig && allowRemove"
        :text="'Remove'"
        type="danger"
        styling-mode="contained"
        @click="onRemoveClick"
        :disabled="removeButtonConfig.disabled"
      />
      <DxButton
        :text="'back'"
        type="default"
        styling-mode="contained"
        @click="popupHidden"
      />
    </div>
  </DxPopup>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";

// Same compact stroke-icon set/shape as gc-popup-distribution-manifest-files.vue's
// getFileIcon, extended with a text/plain entry (BR-2092-6 covers TXT; none of this
// codebase's existing icon-by-type maps did).
const DOCUMENT_ICONS = {
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#155EEF" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13h1l1 4 1-4h1l1 4 1-4h1"/></svg>',
  "application/msword":
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#155EEF" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13h1l1 4 1-4h1l1 4 1-4h1"/></svg>',
  "application/vnd.ms-excel":
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#079455" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13l4 6M12 13l-4 6"/></svg>',
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#079455" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13l4 6M12 13l-4 6"/></svg>',
  "text/csv":
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#079455" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13h8M8 16h8M8 19h5"/></svg>',
  "text/plain":
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#667085" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/><path d="M8 13h8M8 17h5"/></svg>',
};
const DOCUMENT_ICON_FALLBACK =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#35BAF6" stroke-width="1.5"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/><path d="M15 2v5h5"/></svg>';

export default {
  mixins: [RouteMixin],
  components: {},
  data() {
    return {};
  },
  props: {
    dataModel: {
      type: Object,
      default: () => {},
    },
    previewVisible: {
      type: Boolean,
      default: false,
    },
    onBackButtonClick: {
      type: Function,
      default: () => {},
    },
    allowDownload: {
      type: Boolean,
      default: false,
    },
    allowRemove: {
      type: Boolean,
      default: false,
    },
    onRemoveImage: {
      type: Function,
      default: () => {},
    },
    contentType: {
      type: String,
      default: null,
    },
    removeConfirmMessage: {
      type: String,
      default: "Are you sure you wants to remove this image?",
    },
  },
  computed: {
    previewImageSource() {
      return `${this.dataModel.PreviewImageSrc}`;
    },
    isImageContent() {
      return !this.contentType || this.contentType.indexOf("image/") === 0;
    },
    isPdfContent() {
      return this.contentType === "application/pdf";
    },
    previewTitle() {
      return this.isImageContent ? "Image Preview" : "File Preview";
    },
    documentIcon() {
      return DOCUMENT_ICONS[this.contentType] || DOCUMENT_ICON_FALLBACK;
    },
    saveButtonConfig() {
      if (!this.allowDownload) {
        return undefined;
      } else
        return {
          text: "Download",
          type: "default",
          clickCallback: async () => {
            var blobUrl = this.dataModel.DownloadApiUrl;
            Object.assign(document.createElement("a"), {
              target: "_blank",
              href: blobUrl,
            }).click();
          },
        };
    },
    removeButtonConfig() {
      return {
        text: "Remove",
        type: "danger",
      };
    },
  },

  methods: {
    async onRemoveClick() {
      let confirmDelete = await this.$confirm(
        this.removeConfirmMessage,
        "Remove Image"
      );

      if (confirmDelete) {
        this.onRemoveImage(this.dataModel.id);
        this.popupHidden();
      }
    },
    popupHidden() {
      this.onBackButtonClick();
    },
  },
};
</script>

<style lang="scss">
.gc-popup-image-preview {
  #imagePreview,
  #pdfPreview {
    margin: auto;
    display: block;
    border: none;
  }

  .gcpi-doc-placeholder {
    height: 500px;
    width: 520px;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .gcpi-doc-icon svg {
      width: 96px;
      height: 96px;
    }

    .gcpi-doc-filename {
      margin-top: 16px;
      font-size: 14px;
      color: #344054;
      word-break: break-word;
      text-align: center;
      max-width: 400px;
    }
  }
  .footer {
    padding-top: 10px;
    bottom: 0;
    height: 45px;
    text-align: right;
  }
  .footer .save-btn {
    margin-right: 10px;
  }
  .custom-title {
    padding-top: 25px;
    padding-bottom: 25px;
  }

  .title-primary {
    font-size: 20px;
    font-weight: 500;
  }

  .title-secondary {
    padding-left: 8px;
    font-size: 16px;
    color: #957f7f;
  }

  .gc-full-screen-popup {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .footer.multi-btn {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 8px;
    padding-right: 18px;

    // gc-popup.vue also defines a global (unscoped) .footer.multi-btn .dx-button
    // rule with flex-grow:1 - this override must stay nested here so the compiled
    // selector (.gc-popup-image-preview .footer.multi-btn .dx-button) is more
    // specific and wins regardless of stylesheet load order.
    .dx-button {
      flex-grow: 0;
    }
  }

  .footer.gc-data-grid {
    justify-content: flex-end !important;
    .dx-button {
      flex-grow: unset !important;
    }
  }

  .dx-popup-fullscreen {
    .dx-toolbar {
      background-color: #03a9f4;
      .dx-item-content.dx-toolbar-item-content {
        color: white !important;
      }
    }
  }
}
</style>
